import { CommonModule, ɵnormalizeQueryParams } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PagesService } from '../../pages/pages.service';

type StatusKind = 'caution' | 'positive' | 'negative';

interface StatusCell {
  text: string;
  active?: boolean;
  kind?: StatusKind;
}

type TableCellValue = string | number | boolean | StatusCell | null | undefined;

export interface TableColumn {
  key: string;
  header: string;
  width?: string;
}

export type TableSortDirection = 'asc' | 'desc' | null;

export interface TableSortChange {
  column: string;
  direction: TableSortDirection;
}

export type TableRow = Record<string, TableCellValue>;

interface ResolvedStatusCell {
  text: string;
  active: boolean;
  kind: StatusKind;
}

type StatusMappingValue = Pick<ResolvedStatusCell, 'text' | 'kind'>;

const STATUS_MAPPING: Record<string, StatusMappingValue> = {
  'ready for review': { text: 'Ready for Review', kind: 'caution' },
  'awaiting consignment review': { text: 'Awaiting consignment review', kind: 'caution' },
  'currently under review': { text: 'Currently under review', kind: 'caution' },
  'review pending completion': { text: 'Review pending completion', kind: 'caution' },
  'conflicting shipto': { text: 'Conflicting shipto', kind: 'caution' },
  'no order data found': { text: 'No order data found', kind: 'negative' },
  completed: { text: 'Completed', kind: 'positive' },
  rejected: { text: 'Rejected', kind: 'negative' },
};

@Component({
  selector: 'app-jjsv-table',
  imports: [CommonModule],
  templateUrl: './jjsv-table.component.html',
  styleUrl: './jjsv-table.component.scss',
})
export class JjsvTable {
  @Input() columns: TableColumn[] = [];
  @Input() rows: TableRow[] = [];
  @Input() ariaLabel = 'Data table';
  @Input() emptyMessage = 'No data available';
  @Input() isSorting = false;
  @Input() sortColumn: string | null = null;
  @Input() sortDirection: TableSortDirection = null;
  @Output() sortChange = new EventEmitter<TableSortChange>();

  constructor(private readonly router: Router, private readonly pagesService: PagesService) {}

  trackByColumn(index: number, column: TableColumn): string {
    return `${column.key}-${index}`;
  }

  trackByRow(index: number): number {
    return index;
  }

  getCellValue(row: TableRow, key: string): string {
    const value = row[key];
    if (value === null || value === undefined) {
      return '-';
    }
    if (this.isStatusCell(value)) {
      return value.text || '-';
    }
    return String(value);
  }

  isStatusColumn(column: TableColumn): boolean {
    return column.key === 'status';
  }

  onSort(column: TableColumn): void {
    if (!this.isSorting) {
      return;
    }

    const currentDirection = this.sortColumn === column.key ? this.sortDirection : null;
    const nextDirection = this.getNextDirection(currentDirection);
    this.sortChange.emit({ column: column.key, direction: nextDirection });
  }

  getSortDirectionFor(column: TableColumn): TableSortDirection {
    if (this.sortColumn !== column.key) {
      return null;
    }

    return this.sortDirection;
  }

  getSortLabel(column: TableColumn): string {
    const direction = this.getSortDirectionFor(column);
    if (direction === 'asc') {
      return 'sorted ascending';
    }
    if (direction === 'desc') {
      return 'sorted descending';
    }
    return 'not sorted';
  }

  getResolvedStatus(value: TableCellValue): ResolvedStatusCell {
    if (this.isStatusCell(value)) {
      const mapped = this.getMappedStatus(value.text);
      const inReviewBasedActive = this.readInReviewActive(value);
      return {
        text: value.text,
        kind: value.kind ?? mapped?.kind ?? 'caution',
        active: value.active ?? inReviewBasedActive ?? true,
      };
    }

    if (typeof value === 'string') {
      const mapped = this.getMappedStatus(value);
      if (mapped) {
        return { text: mapped.text, kind: mapped.kind, active: true };
      }
      return { text: value, kind: 'caution', active: true };
    }

    return { text: '-', kind: 'caution', active: false };
  }

  isLoadingReviewOrder = false;

  /**
   * Navigates to the review order page with the provided purchase order data.
   * Extracts the PO number from the row data and passes it in the route.
   * The review-orders-page will then use this cartId to fetch fresh API data,
   * ensuring data is retained even on page reload.
   */
  goToReviewOrder(data: TableRow): void {
    if (this.isLoadingReviewOrder) {
      return;
    }

    const cartId = data['cartId'] as string;
    if (!cartId) {
      console.error('Cannot navigate: cartId is missing from row data');
      return;
    }
    this.isLoadingReviewOrder = true;
    this.router.navigate(['/review-order'], {
      queryParams: { cartId },
    }).finally(() => {
      this.isLoadingReviewOrder = false;
    });
  }

  private getNextDirection(currentDirection: TableSortDirection): TableSortDirection {
    if (currentDirection === null) {
      return 'asc';
    }
    if (currentDirection === 'asc') {
      return 'desc';
    }
    return 'asc';
  }

  private getMappedStatus(status: string): StatusMappingValue | undefined {
    return STATUS_MAPPING[status.trim().toLowerCase()];
  }

  private isStatusCell(value: TableCellValue): value is StatusCell {
    return typeof value === 'object' && value !== null && 'text' in value;
  }

  private readInReviewActive(value: StatusCell): boolean | undefined {
      const extendedValue = value as StatusCell & { inReview?: unknown };
      if (!('inReview' in extendedValue) || typeof extendedValue.inReview !== 'boolean') {
        return undefined;
      }

      return !extendedValue.inReview;
  }
}
